import { useEffect, useState } from 'react';
import { PropsWithClassName } from '@/types/PropsWithClassName';
import { cn } from '@/utils/classNames.ts';
import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/24/solid';
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  InitialTableState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';

type DataTableProps<T> = {
  rowClassName?: string;
  headClassName?: string;
  wrapperClassName?: string;
  zebra?: boolean;
  data: T[];
  columns: ColumnDef<T, any>[];
  filters?: ColumnFiltersState;
  initialState?: InitialTableState;
};
function DataTable<T>({
  className,
  rowClassName,
  headClassName,
  wrapperClassName,
  zebra,
  data,
  columns,
  filters,
  initialState,
}: PropsWithClassName<DataTableProps<T>>) {
  const [sorting, setSorting] = useState<SortingState>(initialState?.sorting ?? []);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(initialState?.columnFilters ?? filters ?? []);

  const table = useReactTable({
    initialState,
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    state: {
      sorting,
      columnFilters,
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  useEffect(() => {
    setColumnFilters(filters ?? []);
  }, [filters]);

  return (
    <div className={cn('w-full overflow-x-auto rounded-b-2xl rounded-t-2xl', wrapperClassName)}>
      <table className={cn('table table-pin-rows bg-base-100 w-full', className)}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className={cn('border-base-300 bg-base-100 border-b', headClassName)}>
              {headerGroup.headers.map((header) => (
                <th key={header.id} className="whitespace-nowrap">
                  <div
                    onClick={() =>
                      header.column.getCanSort()
                        ? header.column.toggleSorting(
                            !(header.column.getIsSorted() === 'desc' || !header.column.getIsSorted())
                          )
                        : null
                    }
                    className={cn('flex items-center gap-2', {
                      'cursor-pointer select-none': header.column.getCanSort(),
                    })}
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getCanSort()
                      ? ({
                          asc: <ArrowUpIcon className="w-3" />,
                          desc: <ArrowDownIcon className="w-3" />,
                          false: <ArrowDownIcon className="w-3" />,
                        }[header.column.getIsSorted() as string] ?? null)
                      : null}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row, index) => (
            <tr
              key={row.id}
              className={cn(
                'border-base-300',
                {
                  'bg-base-300': zebra && index % 2 === 0,
                },
                rowClassName
              )}
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default DataTable;
